/**
 * Financial Service - Handles CRUD operations for financial institutions using Firestore
 *
 * ENCRYPTION: Account numbers and routing numbers are encrypted using AES-256-GCM
 * before being stored in Firestore. Data is decrypted when retrieved.
 */

import {
  collection,
  doc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  deleteField,
  onSnapshot,
  Unsubscribe
} from 'firebase/firestore';
import { db, auth } from '../config/firebase';
import { FinancialInstitution, FinancialAccount } from '../types/financial';
import { encryptionService } from './encryptionService';
import { ProfileService } from './profileService';

/**
 * Check if user is authenticated with Firebase
 * Returns true if there's a current Firebase user AND localStorage indicates authenticated
 */
function isAuthenticated(): boolean {
  const hasFirebaseUser = !!auth.currentUser;
  const hasLocalAuth = localStorage.getItem('isAuthenticated') === 'true';
  return hasFirebaseUser && hasLocalAuth;
}

/** Returns the subcollection ref for a profile's financial institutions. */
function financialCollection(profileId: string) {
  return collection(db, 'profiles', profileId, 'financial');
}

/** Returns a doc ref within the financial subcollection. */
function financialDoc(profileId: string, docId: string) {
  return doc(db, 'profiles', profileId, 'financial', docId);
}

class FinancialService {
  private cachedInstitutions: Map<string, FinancialInstitution[]> = new Map();
  private unsubscribes: Map<string, Unsubscribe> = new Map();
  private subscriberCallbacks: Map<string, (institutions: FinancialInstitution[]) => void> = new Map();
  /** Prevents stale onSnapshot from overwriting local cache — see insuranceService for docs. */
  private _dataVersion = 0;
  /**
   * Serializes ALL Firestore writes per document ID. Every write (create, update)
   * chains after the previous write for the same document, preventing races where
   * two concurrent updateDoc calls overwrite each other (e.g. agent sync vs curated portals).
   */
  private _pendingWrites = new Map<string, Promise<void>>();

  /** Notify active subscriber with current cache (instant UI update after mutations) */
  private notifySubscribers(profileId: string) {
    const callback = this.subscriberCallbacks.get(profileId);
    const cached = this.cachedInstitutions.get(profileId) || [];
    if (callback) callback([...cached]);
  }

  /**
   * Encrypts sensitive fields in accounts before storing
   */
  private async encryptAccounts(accounts: FinancialAccount[] | undefined, userId: string): Promise<FinancialAccount[] | undefined> {
    if (!accounts || accounts.length === 0) return accounts;
    if (!userId) {
      console.warn('[FinancialService] No userId provided for encryption');
      return accounts;
    }

    return Promise.all(accounts.map(async (account) => ({
      ...account,
      accountNumber: account.accountNumber
        ? await encryptionService.encrypt(account.accountNumber, userId)
        : account.accountNumber,
      routingNumber: account.routingNumber
        ? await encryptionService.encrypt(account.routingNumber, userId)
        : account.routingNumber,
    })));
  }

  /**
   * Decrypts sensitive fields in accounts after retrieval
   */
  private async decryptAccounts(accounts: FinancialAccount[] | undefined, userId: string): Promise<FinancialAccount[] | undefined> {
    if (!accounts || accounts.length === 0) return accounts;

    return Promise.all(accounts.map(async (account) => ({
      ...account,
      accountNumber: account.accountNumber
        ? await encryptionService.decrypt(account.accountNumber, userId)
        : account.accountNumber,
      routingNumber: account.routingNumber
        ? await encryptionService.decrypt(account.routingNumber, userId)
        : account.routingNumber,
    })));
  }

  /**
   * Decrypts a single institution's accounts
   */
  private async decryptInstitution(institution: FinancialInstitution, userId: string): Promise<FinancialInstitution> {
    return {
      ...institution,
      accounts: await this.decryptAccounts(institution.accounts, userId),
    };
  }

  /**
   * Gets the encryption user ID from ProfileService
   * Uses profile owner ID for consistency across all data access
   */
  private getCurrentUserId(): string {
    return ProfileService.getEncryptionUserId();
  }

  /**
   * Pre-fetch financial institutions so data is cached before page mounts.
   * Called during app initialization in ProfileContext.
   */
  async prefetch(profileId: string): Promise<void> {
    if (!isAuthenticated()) return;
    if (this.cachedInstitutions.has(profileId)) return; // Already cached
    await this.listAsync(profileId);
  }

  // Subscribe to real-time updates for a profile's financial institutions
  subscribeToInstitutions(profileId: string, callback: (institutions: FinancialInstitution[]) => void): Unsubscribe {
    // CRITICAL: Check authentication before making Firestore calls
    // This prevents permission errors when user is not logged in (e.g., Private Guest Mode)
    if (!isAuthenticated()) {
      setTimeout(() => callback([]), 0);
      return () => {}; // Return no-op unsubscribe
    }

    // Store callback for instant notification on cache mutations
    this.subscriberCallbacks.set(profileId, callback);

    // Immediately return pre-fetched data if available (instant load)
    // Includes empty arrays — so empty state shows instantly after prefetch
    const prefetched = this.cachedInstitutions.get(profileId);
    if (prefetched) {
      setTimeout(() => callback(prefetched), 0);
    }

    // Unsubscribe from previous listener if exists
    const existingUnsub = this.unsubscribes.get(profileId);
    if (existingUnsub) {
      existingUnsub();
    }

    const userId = this.getCurrentUserId();

    const unsubscribe = onSnapshot(financialCollection(profileId), async (snapshot) => {
      const myVersion = ++this._dataVersion;

      const institutions: FinancialInstitution[] = [];
      snapshot.forEach((docSnap) => {
        institutions.push({ id: docSnap.id, ...docSnap.data() } as FinancialInstitution);
      });

      // Decrypt accounts for each institution
      const decryptedInstitutions = await Promise.all(
        institutions.map(inst => this.decryptInstitution(inst, userId))
      );

      // A local mutation happened during decryption — skip stale snapshot
      if (this._dataVersion !== myVersion) return;

      // Sort by name
      decryptedInstitutions.sort((a, b) => a.name.localeCompare(b.name));
      this.cachedInstitutions.set(profileId, decryptedInstitutions);
      callback(decryptedInstitutions);
    }, (_error) => {
      // Silently handle permission errors - they're expected during auth token validation
      // Don't call callback with empty array - it would overwrite successfully loaded data
      // The initial listAsync fetch will have already loaded the data
    });

    this.unsubscribes.set(profileId, unsubscribe);
    return unsubscribe;
  }

  // Get all financial institutions for a profile (async, from Firestore)
  async listAsync(profileId: string): Promise<FinancialInstitution[]> {
    // CRITICAL: Check authentication before making Firestore calls
    if (!isAuthenticated()) {
      return [];
    }

    try {
      const snapshot = await getDocs(financialCollection(profileId));
      const userId = this.getCurrentUserId();

      const institutions: FinancialInstitution[] = [];
      snapshot.forEach((docSnap) => {
        institutions.push({ id: docSnap.id, ...docSnap.data() } as FinancialInstitution);
      });

      // Decrypt accounts for each institution
      const decryptedInstitutions = await Promise.all(
        institutions.map(inst => this.decryptInstitution(inst, userId))
      );

      // Sort by name
      decryptedInstitutions.sort((a, b) => a.name.localeCompare(b.name));
      this.cachedInstitutions.set(profileId, decryptedInstitutions);
      return decryptedInstitutions;
    } catch (error) {
      console.error('Error loading financial institutions:', error);
      return [];
    }
  }

  // Get all financial institutions (sync, from cache)
  list(profileId: string): FinancialInstitution[] {
    return this.cachedInstitutions.get(profileId) || [];
  }

  // Create new financial institution
  create(profileId: string, institution: Omit<FinancialInstitution, 'id' | 'createdAt' | 'updatedAt'>): FinancialInstitution {
    // Check for duplicate financial institution (same name and type)
    const cached = this.cachedInstitutions.get(profileId) || [];
    const isDuplicate = cached.some(existingInst => {
      return (
        existingInst.name.toLowerCase().trim() === institution.name.toLowerCase().trim() &&
        existingInst.institutionType === institution.institutionType
      );
    });

    if (isDuplicate) {
      throw new Error('This financial institution already exists in your profile');
    }

    const id = `fi_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
    const now = new Date().toISOString();
    const userId = this.getCurrentUserId();

    const newInstitution: FinancialInstitution = {
      ...institution,
      id,
      createdAt: now,
    };

    // If this is primary, unset others
    if (newInstitution.isPrimary) {
      cached.forEach(inst => {
        if (inst.isPrimary) {
          updateDoc(financialDoc(profileId, inst.id), { isPrimary: false }).catch(error => {
            console.error('Error unsetting primary institution:', error);
          });
        }
      });
    }

    // Helper to recursively remove undefined values from an object/array
    const removeUndefined = (obj: any): any => {
      if (Array.isArray(obj)) {
        return obj.map(item => removeUndefined(item));
      }
      if (obj !== null && typeof obj === 'object') {
        return Object.fromEntries(
          Object.entries(obj)
            .filter(([_, v]) => v !== undefined)
            .map(([k, v]) => [k, removeUndefined(v)])
        );
      }
      return obj;
    };

    // Save to Firestore with encrypted accounts (async, but we return immediately for responsive UI).
    // Serialize via _pendingWrites so subsequent update() calls wait for this to complete.
    const prev = this._pendingWrites.get(id) || Promise.resolve();
    const docRef = financialDoc(profileId, id);
    const writePromise = prev.then(() =>
      this.encryptAccounts(newInstitution.accounts, userId).then(encryptedAccounts => {
        const institutionToSave = removeUndefined({
          ...newInstitution,
          accounts: encryptedAccounts,
        });
        return setDoc(docRef, institutionToSave);
      })
    ).catch(error => {
      console.error('Error encrypting/saving financial institution to Firestore:', error);
      return setDoc(docRef, removeUndefined(newInstitution)).catch(err => {
        console.error('Error saving financial institution to Firestore:', err);
      });
    });
    this._pendingWrites.set(id, writePromise);

    // Update cache immediately for responsive UI (with unencrypted data for display)
    this._dataVersion++;
    if (newInstitution.isPrimary) {
      cached.forEach(inst => (inst.isPrimary = false));
    }
    cached.push(newInstitution);
    cached.sort((a, b) => a.name.localeCompare(b.name));
    this.cachedInstitutions.set(profileId, cached);
    this.notifySubscribers(profileId);

    return newInstitution;
  }

  // Update financial institution
  update(profileId: string, id: string, updates: Partial<FinancialInstitution>): FinancialInstitution {
    const cached = this.cachedInstitutions.get(profileId) || [];
    const index = cached.findIndex(inst => inst.id === id);
    if (index === -1) throw new Error('Institution not found');

    const now = new Date().toISOString();
    const userId = this.getCurrentUserId();

    // Prepare updates for cache - remove undefined values from the merged object
    const cleanedUpdates = Object.fromEntries(
      Object.entries(updates).filter(([_, v]) => v !== undefined)
    );
    const updatedInstitution = { ...cached[index], ...cleanedUpdates, updatedAt: now };

    // Check for duplicate (excluding current institution being updated)
    if (updates.name || updates.institutionType) {
      const isDuplicate = cached.some((existingInst, idx) => {
        if (idx === index) return false; // Skip the current institution
        return (
          existingInst.name.toLowerCase().trim() === updatedInstitution.name.toLowerCase().trim() &&
          existingInst.institutionType === updatedInstitution.institutionType
        );
      });

      if (isDuplicate) {
        throw new Error('This financial institution already exists in your profile');
      }
    }

    // If setting as primary, unset others
    if (updates.isPrimary) {
      cached.forEach(inst => {
        if (inst.id !== id && inst.isPrimary) {
          inst.isPrimary = false;
          updateDoc(financialDoc(profileId, inst.id), { isPrimary: false }).catch(error => {
            console.error('Error unsetting primary institution:', error);
          });
        }
      });
    }

    // Update cache immediately (with unencrypted data for display)
    this._dataVersion++;
    cached[index] = updatedInstitution;
    this.cachedInstitutions.set(profileId, cached);
    this.notifySubscribers(profileId);

    // Helper to recursively remove undefined values from an object/array
    const removeUndefined = (obj: any): any => {
      if (Array.isArray(obj)) {
        return obj.map(item => removeUndefined(item));
      }
      if (obj !== null && typeof obj === 'object') {
        return Object.fromEntries(
          Object.entries(obj)
            .filter(([_, v]) => v !== undefined)
            .map(([k, v]) => [k, removeUndefined(v)])
        );
      }
      return obj;
    };

    // Prepare Firestore updates - convert undefined to deleteField()
    // and encrypt accounts if they're being updated
    const prepareFirestoreUpdates = async () => {
      const firestoreUpdates: any = { updatedAt: now };

      for (const [key, value] of Object.entries(updates)) {
        if (value === undefined) {
          firestoreUpdates[key] = deleteField();
        } else if (key === 'accounts' && Array.isArray(value)) {
          // Encrypt accounts before saving, then remove any undefined values
          const encryptedAccounts = await this.encryptAccounts(value as FinancialAccount[], userId);
          firestoreUpdates[key] = removeUndefined(encryptedAccounts);
        } else {
          firestoreUpdates[key] = removeUndefined(value);
        }
      }

      return firestoreUpdates;
    };

    // Save to Firestore with encrypted data (async).
    // Serialize via _pendingWrites — waits for any prior create or update on this doc.
    const prevWrite = this._pendingWrites.get(id) || Promise.resolve();
    const writePromise = prevWrite.then(() =>
      prepareFirestoreUpdates().then(firestoreUpdates =>
        updateDoc(financialDoc(profileId, id), firestoreUpdates)
      )
    ).catch(error => {
      console.error('Error updating financial institution in Firestore:', error);
    });
    this._pendingWrites.set(id, writePromise);

    return updatedInstitution;
  }

  // Delete financial institution
  delete(profileId: string, id: string): void {
    // Update cache immediately
    this._dataVersion++;
    const cached = this.cachedInstitutions.get(profileId) || [];
    this.cachedInstitutions.set(profileId, cached.filter(inst => inst.id !== id));
    this.notifySubscribers(profileId);

    // Delete from Firestore (async)
    deleteDoc(financialDoc(profileId, id)).catch(error => {
      console.error('Error deleting financial institution from Firestore:', error);
    });
  }

  // Cleanup subscriptions
  cleanup(profileId?: string): void {
    if (profileId) {
      const unsub = this.unsubscribes.get(profileId);
      if (unsub) {
        unsub();
        this.unsubscribes.delete(profileId);
      }
      this.cachedInstitutions.delete(profileId);
      this.subscriberCallbacks.delete(profileId);
    } else {
      // Cleanup all
      this.unsubscribes.forEach(unsub => unsub());
      this.unsubscribes.clear();
      this.cachedInstitutions.clear();
      this.subscriberCallbacks.clear();
    }
  }
}

export const financialService = new FinancialService();
